import { useRef, useCallback, useEffect } from "react";

/**
 * useDrag — encapsulates pointer/touch drag state for a range input.
 * Returns { inputRef, localVal, handleInput, handleCommit }.
 *
 * The `dampened` flag applies a 0.35 movement factor on touch devices,
 * making sliders less twitchy for fine adjustments.
 */
export function useDrag({ value, onDrag, onCommit, min, max, dampened }) {
  const isDragging = useRef(false);
  const latestVal = useRef(value);
  const inputRef = useRef(null);
  const touchDragRef = useRef(null);
  const localValRef = useRef(value);

  // Stable ref for callbacks — avoids stale closures in event listeners
  const cbRef = useRef({ onDrag, onCommit, min, max });
  cbRef.current = { onDrag, onCommit, min, max };

  // Sync external value when not dragging
  const syncFromProp = useCallback((propValue) => {
    if (!isDragging.current) {
      latestVal.current = propValue;
      localValRef.current = propValue;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInput = useCallback((e) => {
    isDragging.current = true;
    const v = Number(e.target.value);
    latestVal.current = v;
    localValRef.current = v;
    if (cbRef.current.onDrag) cbRef.current.onDrag(v);
    return v;
  }, []);

  const handleCommit = useCallback(() => {
    if (isDragging.current) {
      isDragging.current = false;
      cbRef.current.onCommit(latestVal.current);
    }
  }, []);

  // Dampened touch handling
  useEffect(() => {
    const el = inputRef.current;
    if (!el || !dampened || !('ontouchstart' in window || navigator.maxTouchPoints > 0)) return;

    const onTouchStart = (e) => {
      const t = e.touches[0];
      touchDragRef.current = {
        startX: t.clientX,
        startVal: latestVal.current,
        w: el.getBoundingClientRect().width
      };
      isDragging.current = true;
    };

    const onTouchMove = (e) => {
      const d = touchDragRef.current;
      if (!d) return;
      e.preventDefault();
      const dx = e.touches[0].clientX - d.startX;
      const { min: mn, max: mx, onDrag: fn } = cbRef.current;
      const nv = Math.round(Math.max(mn, Math.min(mx,
        d.startVal + (dx * 0.35 / d.w) * (mx - mn)
      )));
      latestVal.current = nv;
      localValRef.current = nv;
      if (fn) fn(nv);
      return nv;
    };

    const onTouchEnd = () => {
      touchDragRef.current = null;
      if (isDragging.current) {
        isDragging.current = false;
        cbRef.current.onCommit(latestVal.current);
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [dampened]);

  return { inputRef, latestVal, localValRef, handleInput, handleCommit, syncFromProp };
}
